import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Field } from '@vnetwork/ui/forms';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Input,
  Card, CardHeader, CardTitle, CardBody,
} from '@vnetwork/ui/primitives';
import { useTicketTypes } from '@/api/tickets-workflow';
import {
  TICKET_TYPE_GROUP_MEETING,
  TICKET_TYPE_GROUP_SUPPORT_REQUEST,
} from '@/api/tickets-constants';
import type { TicketFormValues } from '../ticket-form-schema';
import { NumericInput } from '@/components/numeric-input';

const twoColGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--vn-space-4)',
} as const;

const TYPE_GROUPS = [
  { value: 'support_request', label: 'Support Request' },
  { value: 'error_service', label: 'Error Service' },
  { value: 'service_request', label: 'Service Request' },
  { value: 'service', label: 'Service' },
  { value: TICKET_TYPE_GROUP_MEETING, label: 'Meeting' },
  { value: 'change', label: 'Change' },
  { value: 'internal', label: 'Internal' },
];

const MEETING_PURPOSE_OPTIONS = [
  { value: 'presale_presentation', label: 'Presale Presentation' },
  { value: 'support', label: 'Support' },
  { value: 'other', label: 'Other' },
];

const MEETING_TYPE_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

interface ClassificationSectionProps {
  /** Disable group + type selects when in edit mode (type group locked on edit). */
  disableGroup?: boolean;
}

export function ClassificationSection({ disableGroup = false }: ClassificationSectionProps) {
  const { control, setValue, getValues, formState: { errors } } = useFormContext<TicketFormValues>();

  const typeGroup = useWatch({ control, name: 'ticket_type_group' });
  const typeKey = useWatch({ control, name: 'ticket_type_key' });
  const isMeeting = typeKey === TICKET_TYPE_GROUP_MEETING;

  const { data: typesRes, isLoading: typesLoading } = useTicketTypes(typeGroup || undefined);
  const ticketTypes = typesRes?.results ?? [];

  const handleGroupChange = (val: string) => {
    setValue('ticket_type_group', val);
    setValue('ticket_type_id', 0);
    setValue('ticket_type_key', '');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Classification</CardTitle>
      </CardHeader>
      <CardBody>
      <div style={twoColGrid}>
        {/* Type group */}
        <Field label="Type Group" required error={String(errors.ticket_type_group?.message ?? '')  || undefined}>
          {(fp) => (
            <Controller
              name="ticket_type_group"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || ''}
                  onValueChange={(v) => {
                    /* Radix echoes onValueChange when the controlled value is set
                       programmatically (reset() on the edit page), and the echo runs
                       against a stale render closure — so compare with the LIVE form
                       value. Only a real group change clears the dependent type. */
                    if (v === getValues('ticket_type_group')) return;
                    field.onChange(v);
                    handleGroupChange(v);
                  }}
                  disabled={disableGroup}
                >
                  <SelectTrigger {...fp} invalid={!!errors.ticket_type_group}>
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_GROUPS.map((g) => (
                      <SelectItem key={g.value} value={g.value}>
                        {g.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          )}
        </Field>

        {/* Ticket type — refetches when group changes */}
        <Field
          label="Ticket Type"
          required
          error={String(errors.ticket_type_id?.message ?? '') || undefined}
        >
          {(fp) => (
            <Controller
              name="ticket_type_id"
              control={control}
              render={({ field }) => {
                /* Resolve the selected label ourselves: options load async, and a
                   disabled (locked) Radix Select never re-resolves SelectValue for
                   items that mounted after the value was set via reset(). */
                const selected = ticketTypes.find((t) => t.ticket_type_id === field.value);
                return (
                <Select
                  value={field.value > 0 ? String(field.value) : ''}
                  onValueChange={(v) => {
                    /* Ignore programmatic clear echoes: there is no empty SelectItem,
                       so v='' can only come from Radix syncing a controlled value. */
                    if (!v) return;
                    const id = Number(v);
                    if (id === getValues('ticket_type_id')) return;
                    const found = ticketTypes.find((t) => t.ticket_type_id === id);
                    field.onChange(id);
                    setValue('ticket_type_key', found?.ticket_type_key ?? '');
                    if (found?.ticket_priority) setValue('ticket_priority', found.ticket_priority);
                  }}
                  disabled={!typeGroup || disableGroup}
                >
                  <SelectTrigger {...fp} invalid={!!errors.ticket_type_id}>
                    <SelectValue placeholder={typesLoading ? 'Loading…' : 'Select type'}>
                      {selected?.ticket_type_title}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ticketTypes.map((t) => (
                      <SelectItem key={t.ticket_type_id} value={String(t.ticket_type_id)}>
                        {t.ticket_type_title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                );
              }}
            />
          )}
        </Field>

        {/* Priority */}
        <Field label="Priority" error={String(errors.ticket_priority?.message ?? '') || undefined}>
          {(fp) => (
            <Controller
              name="ticket_priority"
              control={control}
              render={({ field }) => (
                <Select value={field.value || ''} onValueChange={field.onChange}>
                  <SelectTrigger {...fp} invalid={!!errors.ticket_priority}>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          )}
        </Field>

        {/* Security tag */}
        <Field label="Security Tag">
          {(fp) => (
            <Controller
              name="ticket_security_tag"
              control={control}
              render={({ field }) => (
                <Select value={field.value || 'none'} onValueChange={field.onChange}>
                  <SelectTrigger {...fp}>
                    <SelectValue placeholder="Select tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          )}
        </Field>

        {/* Meeting-only fields */}
        {isMeeting && (
          <>
            <Field
              label="Meeting Purpose"
              required
              error={String(errors.ticket_meeting_purpose?.message ?? '') || undefined}
            >
              {(fp) => (
                <Controller
                  name="ticket_meeting_purpose"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value || ''} onValueChange={field.onChange}>
                      <SelectTrigger {...fp} invalid={!!errors.ticket_meeting_purpose}>
                        <SelectValue placeholder="Select purpose" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEETING_PURPOSE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}
            </Field>

            <Field label="Meeting Type">
              {(fp) => (
                <Controller
                  name="ticket_meeting_type"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value || 'online'} onValueChange={field.onChange}>
                      <SelectTrigger {...fp}>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEETING_TYPE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}
            </Field>

            <Field
              label="Meeting Link / Location"
              required
              error={String(errors.ticket_meeting_via?.message ?? '') || undefined}
              style={{ gridColumn: 'span 2' }}
            >
              {(fp) => (
                <Controller
                  name="ticket_meeting_via"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...fp}
                      {...field}
                      placeholder="https://meet.google.com/… or room name"
                      invalid={!!errors.ticket_meeting_via}
                    />
                  )}
                />
              )}
            </Field>
          </>
        )}

        {/* Ticket type for support_request — show request picker (ticket_request_id) */}
        {typeGroup === TICKET_TYPE_GROUP_SUPPORT_REQUEST && (
          <Field label="Request Category">
            {(fp) => (
              <Controller
                name="ticket_request_id"
                control={control}
                render={({ field }) => (
                  <NumericInput thousandSeparator={false} precision={0}
                    {...fp}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    placeholder="Request ID (optional)"
                  />
                )}
              />
            )}
          </Field>
        )}
      </div>
      </CardBody>
    </Card>
  );
}
